import type { ReactNode } from 'react';
import { DocumentReviewSection } from './document-review-section';
import { SignatureSection } from './signature-section';
import { VerificationSection } from './verification-section';
import { SignCommitSection } from './sign-commit-section';
import type { useSigning } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signing';

type SigningState = ReturnType<typeof useSigning>;

/**
 * The four stages of the signing flow, as layout sections.
 *
 * Each stage is a separate server call and none of them completes the execution on its own.
 */
export function buildSigningSections(input: {
  executionId: string;
  contentHash: string;
  signing: SigningState;
  onSigned: () => void;
  onBack: () => void;
}): Array<{ id: string; label: string; content: ReactNode }> {
  const { signing } = input;
  return [
    {
      id: 'document',
      label: 'Document',
      content: <DocumentReviewSection contentHash={input.contentHash} />,
    },
    {
      id: 'signature',
      label: 'Signature',
      content: (
        <SignatureSection
          executionId={input.executionId}
          ensureSession={signing.ensureSession}
          signingMethod={signing.signingMethod}
          onMethodChange={signing.setSigningMethod}
          onUploaded={(objectKey, mimeType, byteSize) =>
            signing.setSignature({ objectKey, mimeType, byteSize })
          }
          signerName={signing.signerName}
          onSignerNameChange={signing.setSignerName}
          signerTitle={signing.signerTitle}
          onSignerTitleChange={signing.setSignerTitle}
        />
      ),
    },
    {
      id: 'verification',
      label: 'Identity check',
      content: (
        <VerificationSection
          executionId={input.executionId}
          ensureSession={signing.ensureSession}
          signingMethod={signing.signingMethod}
          onVerified={signing.setVerificationId}
        />
      ),
    },
    {
      id: 'commit',
      label: 'Sign',
      content: (
        <SignCommitSection
          error={signing.error ?? signing.sessionError}
          busy={signing.busy}
          canSign={signing.canSign}
          onSign={input.onSigned}
          onBack={input.onBack}
        />
      ),
    },
  ];
}
